import { Controller } from "@hotwired/stimulus"

// Autocompletes the food-name field against past entries and fills calories
// when a suggestion is chosen. Replaces the former Alpine/CDN implementation.
export default class extends Controller {
  static targets = ["input", "calories", "dropdown"]

  connect() {
    this.results = []
    this.highlightedIndex = -1
    this.debounceTimer = null
  }

  disconnect() {
    clearTimeout(this.debounceTimer)
  }

  search() {
    clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.performSearch(), 300)
  }

  async performSearch() {
    const term = this.inputTarget.value.trim()
    if (term.length < 2) return this.close()

    try {
      const response = await fetch(`/food_logs/search?term=${encodeURIComponent(term)}`)
      this.results = await response.json()
      this.highlightedIndex = -1
      this.render()
    } catch (error) {
      console.error("Food search failed:", error)
      this.close()
    }
  }

  onFocus() {
    if (this.results.length > 0) this.render()
  }

  onBlur() {
    // Delay so a click/tap on a suggestion registers before we hide it.
    this.blurTimer = setTimeout(() => this.close(), 150)
  }

  onKeydown(event) {
    if (!this.isOpen) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.highlight(Math.min(this.highlightedIndex + 1, this.results.length - 1))
        break
      case "ArrowUp":
        event.preventDefault()
        this.highlight(Math.max(this.highlightedIndex - 1, -1))
        break
      case "Enter":
        if (this.highlightedIndex >= 0) {
          event.preventDefault()
          this.select(this.results[this.highlightedIndex])
        }
        break
      case "Escape":
        this.close()
        break
    }
  }

  select(item) {
    if (!item) return
    this.inputTarget.value = item.value
    if (this.hasCaloriesTarget) this.caloriesTarget.value = item.calories
    this.close()
  }

  // --- rendering -----------------------------------------------------------

  render() {
    if (this.results.length === 0) return this.close()

    this.dropdownTarget.innerHTML = ""
    this.results.forEach((item, index) => {
      const row = document.createElement("div")
      row.className = "food-dropdown__item"
      if (index === this.highlightedIndex) row.classList.add("is-active")
      const freqLabel = item.frequency > 1 ? ` · ${item.frequency}×` : ""
      row.innerHTML = `<span class="fw-medium">${escapeHtml(item.value)}</span>` +
        `<span class="text-muted small text-nowrap">${escapeHtml(String(item.calories))} cal${freqLabel}</span>`

      // Use mousedown so selection happens before the input's blur fires.
      row.addEventListener("mousedown", (event) => {
        event.preventDefault()
        this.select(item)
      })
      row.addEventListener("mouseenter", () => this.highlight(index))
      this.dropdownTarget.appendChild(row)
    })

    this.dropdownTarget.hidden = false
  }

  highlight(index) {
    this.highlightedIndex = index
    Array.from(this.dropdownTarget.children).forEach((child, i) => {
      child.classList.toggle("is-active", i === index)
    })
  }

  close() {
    clearTimeout(this.blurTimer)
    this.dropdownTarget.hidden = true
    this.highlightedIndex = -1
  }

  get isOpen() {
    return !this.dropdownTarget.hidden
  }
}

function escapeHtml(value) {
  const div = document.createElement("div")
  div.textContent = value
  return div.innerHTML
}
